import React from "react";
import { Link } from "react-router-dom";
import { HeartIcon } from "@heroicons/react/24/outline";
import { HeartIcon as HeartSolidIcon } from "@heroicons/react/24/solid";
import { useAuth } from "../../context/AuthContext";
import { wishlistAPI } from "../../services/api";
import { resolveImageUrl } from "../../utils/image";
import toast from "react-hot-toast";

interface Product {
  id: string;
  name: string;
  slug: string;
  price: number;
  original_price?: number;
  images: { url: string; is_primary: boolean }[];
  is_featured?: boolean;
  total_stock: number;
  category?: string;
}

interface ProductCardProps {
  product: Product;
}

export default function ProductCard({ product }: ProductCardProps) {
  const { isAuthenticated } = useAuth();
  const [wishlisted, setWishlisted] = React.useState(false);

  const primaryImage = product.images.find((i) => i.is_primary) || product.images[0];
  const imageUrl = resolveImageUrl(primaryImage?.url, "https://via.placeholder.com/400x500/f9a8d4/ec4899?text=Dress");

  const discount = product.original_price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : 0;

  const toggleWishlist = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error("Please login to add to wishlist");
      return;
    }
    try {
      if (wishlisted) {
        await wishlistAPI.remove(product.id);
        setWishlisted(false);
        toast.success("Removed from wishlist");
      } else {
        await wishlistAPI.add(product.id);
        setWishlisted(true);
        toast.success("Added to wishlist!");
      }
    } catch {
      toast.error("Something went wrong");
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-sm group hover:shadow-lg transition-shadow duration-200 cursor-pointer">
      <Link to={`/products/${product.slug}`}>
        <div className="relative bg-gray-50 overflow-hidden" style={{ paddingBottom: "100%" }}>
          <img
            src={imageUrl}
            alt={product.name}
            className="absolute inset-0 w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
          {discount > 0 && (
            <span className="absolute top-2 left-0 bg-green-600 text-white text-xs font-bold px-1.5 py-0.5">
              {discount}% off
            </span>
          )}
          {product.total_stock === 0 && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
              <span className="text-gray-500 font-semibold text-sm border border-gray-300 px-2 py-1">Out of Stock</span>
            </div>
          )}
          <button
            onClick={toggleWishlist}
            className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity border border-gray-100"
          >
            {wishlisted ? (
              <HeartSolidIcon className="h-4 w-4 text-primary-600" />
            ) : (
              <HeartIcon className="h-4 w-4 text-gray-400" />
            )}
          </button>
        </div>
      </Link>
      <div className="p-2.5">
        <Link to={`/products/${product.slug}`}>
          <h3 className="text-sm text-gray-700 line-clamp-2 mb-1 hover:text-primary-600">
            {product.name}
          </h3>
        </Link>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-base font-bold text-gray-900">
            ₹{Number(product.price).toLocaleString("en-IN")}
          </span>
          {product.original_price && (
            <>
              <span className="text-xs text-gray-400 line-through">
                ₹{Number(product.original_price).toLocaleString("en-IN")}
              </span>
              {discount > 0 && (
                <span className="text-xs text-green-600 font-medium">{discount}% off</span>
              )}
            </>
          )}
        </div>
        {product.is_featured && (
          <p className="text-xs text-primary-500 mt-0.5">Trending</p>
        )}
      </div>
    </div>
  );
}
